'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function ApacheLogAnalyzer() {
  const [domain, setDomain] = useState('');
  const [errorCode, setErrorCode] = useState('403');
  const [copied, setCopied] = useState(false);

  const errorCodes = [
    { code: '400', label: '400 - Bad Request' },
    { code: '401', label: '401 - Unauthorized' },
    { code: '403', label: '403 - Forbidden' },
    { code: '404', label: '404 - Not Found' },
    { code: '500', label: '500 - Internal Server Error' },
    { code: '502', label: '502 - Bad Gateway' },
    { code: '503', label: '503 - Service Unavailable' },
    { code: '504', label: '504 - Gateway Timeout' },
  ];

  const generateCommand = () => {
    if (!domain) return '';

    return `find /var/www/vhosts/system/${domain}/logs/ -type f -iname '*log*' -exec cat {} \\; | awk '($9 == "${errorCode}"){print $1,$3,$4,$5,$7,$8,$9,$18}'`;
  };

  const command = generateCommand();

  const copyToClipboard = async () => {
    if (!command) return;

    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const explainCommand = () => {
    if (!domain) return null;

    return (
      <div className="text-sm text-gray-600 space-y-2">
        <p className="font-semibold">Was macht dieser Befehl?</p>
        <ul className="list-disc list-inside space-y-1">
          <li><code className="text-xs bg-gray-100 px-1 rounded">find /var/www/vhosts/system/{domain}/logs/</code> - Sucht im Log-Verzeichnis</li>
          <li><code className="text-xs bg-gray-100 px-1 rounded">-type f -iname '*log*'</code> - Findet alle Log-Dateien (case-insensitive)</li>
          <li><code className="text-xs bg-gray-100 px-1 rounded">-exec cat</code> - Liest den Inhalt aller gefundenen Logs</li>
          <li><code className="text-xs bg-gray-100 px-1 rounded">awk '($9 == "{errorCode}")'</code> - Filtert nach HTTP-Fehlercode {errorCode}</li>
          <li><code className="text-xs bg-gray-100 px-1 rounded">print $1,$3,...</code> - Zeigt nur relevante Felder (IP, User, Zeit, Request, Status)</li>
        </ul>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Apache Log Analyzer
      </h3>
      <p className="text-gray-700 mb-6">
        Generiere Shell-Befehle zum Analysieren von Apache-Logs. Perfekt für Plesk/cPanel Server.
      </p>

      {/* Inputs */}
      <div className="space-y-4 mb-6">
        <div>
          <label htmlFor="domain" className="block text-sm font-semibold mb-2">
            Domain Name
          </label>
          <input
            type="text"
            id="domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Ohne www, nur die Domain (z.B. example.com)
          </p>
        </div>

        <div>
          <label htmlFor="errorCode" className="block text-sm font-semibold mb-2">
            HTTP Fehlercode
          </label>
          <select
            id="errorCode"
            value={errorCode}
            onChange={(e) => setErrorCode(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {errorCodes.map((ec) => (
              <option key={ec.code} value={ec.code}>
                {ec.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Generated Command */}
      {command && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold">
                Generierter Befehl
              </label>
              <button
                onClick={copyToClipboard}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-accent hover:bg-accent/90 text-white'
                )}
              >
                {copied ? '✓ Kopiert!' : 'Kopieren'}
              </button>
            </div>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <code className="text-sm font-mono whitespace-pre-wrap break-all">
                {command}
              </code>
            </div>
          </div>

          {/* Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            {explainCommand()}
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Hinweis:</strong> Dieser Befehl setzt voraus, dass deine Logs im Plesk-Standard-Pfad liegen.
              Passe den Pfad ggf. an deine Server-Konfiguration an.
            </p>
          </div>

          {/* Usage Instructions */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="font-semibold text-sm mb-2">So verwendest du den Befehl:</p>
            <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
              <li>Kopiere den Befehl oben</li>
              <li>Verbinde dich per SSH mit deinem Server</li>
              <li>Füge den Befehl ein und drücke Enter</li>
              <li>Du siehst alle Requests mit dem Fehlercode {errorCode} für {domain}</li>
            </ol>
          </div>
        </div>
      )}

      {!command && (
        <div className="text-center text-gray-500 py-8">
          Gib eine Domain ein, um den Befehl zu generieren
        </div>
      )}
    </div>
  );
}
